import { UploadFile } from 'antd/lib/upload/interface';
import { AxiosRequestConfig } from 'axios';
import { stringify } from 'query-string';
import { FileAttachment, FileAttachmentV2 } from 'api/attachments';
import { HttpClient } from 'api/http-client';
import { UpdateGroupFormProps } from 'components/organisms/files/update-group/modal';
import { FileGroup } from './types';

export enum FileGroupSourceType {
  Company = 'COMPANY',
  Project = 'PROJECT',
}
export type FileGroupSource = {
  sourceId: number;
  sourceType: FileGroupSourceType;
};
export type UploadFilePayload = { groupId: number; source: FileGroupSource };
type UploadFileResponse = {
  attachment: FileAttachment;
};
type CreateFileGroupPayload = Pick<FileGroup, 'folderName' | 'isPrivate'> &
  FileGroupSource;

export type RearrangeFilesPayload = {
  attachmentId: number;
  newPosition: number;
  folderId: number | null;
};

export type RearrangeFoldersPayload = {
  folderId: number;
  newPosition: number;
};

class FilesHttpClient extends HttpClient {
  public async createFileGroup(
    payload: CreateFileGroupPayload,
  ): Promise<FileGroup> {
    return this.post(
      `/folders/${payload.sourceType}/${payload.sourceId}`,
      payload,
    );
  }

  public getFileGroups(source: FileGroupSource): Promise<FileGroup[]> {
    return this.get(`/folders?${stringify(source)}`);
  }

  public getBulkProjectFileGroups(
    projectIds: number[],
  ): Promise<Record<number, FileGroup[]>> {
    return this.get(
      `/folders/bulkList?${stringify({
        sourceType: 'PROJECT',
        sourceIds: projectIds.join(','),
      })}`,
    );
  }

  public getBulkProjectAttachments(
    projectIds: number[],
  ): Promise<Record<number, FileAttachmentV2[]>> {
    return this.get(
      `/attachments/projects?${stringify({
        projectIds: projectIds.join(','),
      })}`,
    );
  }

  public async uploadFile(
    file: UploadFile,
    data: UploadFilePayload,
    config: AxiosRequestConfig,
  ): Promise<FileAttachment> {
    if (!file.originFileObj) {
      return Promise.reject(new Error('file object is empty'));
    }
    const formData = new FormData();
    const fileMeta = {
      attachment: {
        name: file.name,
        publicVisibility: true,
      },
      associatedEntity: {
        sourceType: data.source.sourceType,
        sourceId: data.source.sourceId,
      },
    };
    const blob = new Blob([JSON.stringify(fileMeta)], {
      type: 'application/json',
    });

    formData.append('file', file.originFileObj);
    formData.append('request', blob);

    const res: UploadFileResponse = await this.post(
      '/attachments',
      formData,
      config,
    );

    return res.attachment;
  }

  public async linkFiles(
    folderId: number,
    fileIds: number[],
    sourceType: string,
    sourceId: number,
    config?: AxiosRequestConfig,
  ): Promise<UploadFile> {
    return this.post(
      `/folders/${folderId}/${sourceType}/${sourceId}/attachments/link`,
      fileIds,
      config,
    );
  }

  public async deleteFile(
    groupId: number,
    fileId: number,
    companyId: number,
  ): Promise<void> {
    return this.delete(`/folders/${groupId}/attachments/company/${companyId}`, {
      data: [fileId],
    });
  }

  public async rearrangeFiles(
    sourceType: string,
    sourceId: number,
    payload: RearrangeFilesPayload,
  ): Promise<void> {
    return this.put(
      `/folders/reorder-attachments/${sourceType}/${sourceId}`,
      payload,
    );
  }

  public async rearrangeFoldersForCompany(
    companyId: number,
    payload: RearrangeFoldersPayload,
  ): Promise<void> {
    return this.put(`/companies/${companyId}/reorder-folder`, payload);
  }

  public async deleteFileGroup(
    groupId: number,
    sourceType: string,
    sourceId: number,
  ): Promise<void> {
    return this.delete(`/folders/${groupId}/${sourceType}/${sourceId}`);
  }

  public async updateFolder(
    groupId: number,
    payload: UpdateGroupFormProps,
    sourceType: string,
    sourceId: number,
  ): Promise<FileGroup> {
    return this.put(`/folders/${groupId}/${sourceType}/${sourceId}`, payload);
  }

  public async changeFilePinnedStatus(
    fileId: number,
    isPinned: boolean,
  ): Promise<void> {
    return this.put(`/attachments/${fileId}`, { isPinned });
  }

  public triggerEmailForFileGroup(groupId: number) {
    return this.get(`/folders/${groupId}/email`);
  }

  public getFileGroupDownloadUrl(groupId: number): string {
    return `${this.BASEURL}/folders/${groupId}/download`;
  }

  public getFileDownloadUrl(groupId: number, fileId: number): string {
    return `${this.BASEURL}/folders/${groupId}/attachments/${fileId}/download`;
  }
}

export const FilesAPI = new FilesHttpClient();
